// Captation pour la secrétaire en direct : micro (+ son de l'onglet en visio) mélangés, ramenés en
// PCM 16 bits mono 16 kHz par un AudioWorklet et envoyés par paquets de 100 ms. Aucune dépendance à React.

const TARGET_RATE = 16000;
const PACKET_SAMPLES = 1600; // 100 ms

// Moyenne des échantillons sur chaque pas de rééchantillonnage : filtre anti-repliement suffisant pour la voix.
const WORKLET_SOURCE = `
class Pcm16k extends AudioWorkletProcessor {
  constructor() {
    super();
    this.step = sampleRate / ${TARGET_RATE};
    this.pos = 0; this.sum = 0; this.count = 0;
    this.out = new Int16Array(${PACKET_SAMPLES}); this.len = 0;
  }
  process(inputs) {
    const channels = inputs[0];
    if (!channels || !channels.length) return true;
    const frames = channels[0].length;
    for (let i = 0; i < frames; i += 1) {
      let sample = 0;
      for (let c = 0; c < channels.length; c += 1) sample += channels[c][i];
      this.sum += sample / channels.length; this.count += 1; this.pos += 1;
      if (this.pos >= this.step) {
        this.pos -= this.step;
        const value = Math.max(-1, Math.min(1, this.sum / this.count));
        this.out[this.len++] = value < 0 ? value * 32768 : value * 32767;
        this.sum = 0; this.count = 0;
        if (this.len === this.out.length) {
          this.port.postMessage(this.out.buffer, [this.out.buffer]);
          this.out = new Int16Array(${PACKET_SAMPLES}); this.len = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('pcm-16k', Pcm16k);
`;

const microConstraints = (source) => ({
  audio: source === 'visio'
    ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    : { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
});

export const liveCaptureSupported = () => Boolean(
  navigator.mediaDevices?.getUserMedia && (window.AudioContext || window.webkitAudioContext)
  && typeof AudioWorkletNode !== 'undefined'
);

export class LiveCapture {
  constructor({ source, onPcm, onLevel, onTabAudioEnded }) {
    this.source = source;
    this.onPcm = onPcm;
    this.onLevel = onLevel;
    this.onTabAudioEnded = onTabAudioEnded;
    this.streams = [];
  }

  async start() {
    let display = null;
    if (this.source === 'visio') {
      display = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: { suppressLocalAudioPlayback: false },
        preferCurrentTab: false,
        selfBrowserSurface: 'exclude',
        systemAudio: 'include',
      });
      this.streams.push(display);
      if (!display.getAudioTracks().length) {
        this.stop();
        throw new Error("Aucun son partagé : choisis l'onglet de la visio et coche « Partager l'audio de l'onglet ».");
      }
      display.getVideoTracks().forEach((track) => { track.enabled = false; });
      display.getAudioTracks()[0].addEventListener('ended', () => this.onTabAudioEnded?.());
    }
    const micro = await navigator.mediaDevices.getUserMedia(microConstraints(this.source)).catch((error) => {
      this.stop();
      throw new Error(error?.name === 'NotAllowedError'
        ? "Accès au micro refusé : autorise-le dans la barre d'adresse du navigateur."
        : "Impossible d'ouvrir le micro.");
    });
    this.streams.push(micro);

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.context = new AudioContextClass();
    const moduleUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
    try {
      await this.context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }
    const mix = this.context.createGain();
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 512;
    [micro, display].filter((stream) => stream?.getAudioTracks().length).forEach((stream) => {
      this.context.createMediaStreamSource(new MediaStream(stream.getAudioTracks())).connect(mix);
    });
    this.worklet = new AudioWorkletNode(this.context, 'pcm-16k', { numberOfInputs: 1, numberOfOutputs: 1 });
    this.worklet.port.onmessage = (event) => this.onPcm?.(event.data);
    const mute = this.context.createGain();
    mute.gain.value = 0; // le nœud doit rejoindre la sortie pour être cadencé, sans rien jouer
    mix.connect(this.analyser);
    mix.connect(this.worklet).connect(mute).connect(this.context.destination);
    this.meter();
  }

  meter() {
    const samples = new Uint8Array(this.analyser.fftSize);
    const tick = () => {
      if (!this.analyser) return;
      this.analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (let i = 0; i < samples.length; i += 1) peak = Math.max(peak, Math.abs(samples[i] - 128));
      this.onLevel?.(Math.min(1, peak / 64));
      this.frame = requestAnimationFrame(tick);
    };
    tick();
  }

  stop() {
    cancelAnimationFrame(this.frame);
    this.analyser = null;
    if (this.worklet) this.worklet.port.onmessage = null;
    this.worklet = null;
    this.streams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    this.streams = [];
    this.context?.close().catch(() => {});
    this.context = null;
  }
}
